import { ApolloClient, from, HttpLink, HttpOptions, split } from "@apollo/client";
import { NormalizedCacheObject } from "@apollo/client/cache/inmemory/types";
import { setContext } from "@apollo/client/link/context";
import { cache } from "./apollo-cache";
import { Auth0ContextInterface } from "@auth0/auth0-react";

import { errorLink } from "./apollo-links";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { createUploadLink } from "apollo-upload-client";
import ApolloLinkNetworkStatus from "react-apollo-network-status/dist/src/ApolloLinkNetworkStatus";
import { ElectronAPI } from "~/electron/api";
import { getWebsocketUrl } from "./websocket-router";

let client: ApolloClient<NormalizedCacheObject>;
const linkOpts: HttpOptions = {
  uri: "/api/graphql",
  credentials: "include",
};

export const getApolloClient = (
  apolloLinkNetworkStatus: ApolloLinkNetworkStatus,
  auth0Context: Auth0ContextInterface
): ApolloClient<NormalizedCacheObject> => {
  if (client) {
    return client;
  }

  const getAccessToken = () =>
    auth0Context.getAccessTokenSilently({
      scope: "openid profile email",
    });

  const buildWsParams = async () => {
    const authToken = await getAccessToken();
    return { authToken };
  };

  const authLink = setContext(async (_, { headers, ...contextOthers }) => {
    const accessToken = await getAccessToken();
    ElectronAPI.sendAuthToken(accessToken);
    return { ...contextOthers, headers: { ...headers, authorization: `Bearer ${accessToken}` } };
  });

  const httpLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    new GraphQLWsLink(
      createClient({
        url: () => getWebsocketUrl(auth0Context),
        connectionParams: buildWsParams,
      })
    ),
    split(
      (operation) => operation.getContext().hasUpload,
      createUploadLink({ ...linkOpts }),
      new HttpLink(linkOpts)
    )
  );

  client = new ApolloClient({
    cache,
    ssrMode: true,
    connectToDevTools: true,
    link: from([authLink, errorLink, apolloLinkNetworkStatus, httpLink]),
    defaultOptions: {
      watchQuery: {
        errorPolicy: "ignore",
      },
      query: {
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });

  return client;
};
